import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bot, Send, ExternalLink, Loader2, AlertTriangle, Paperclip, X, Globe, FileText, Image as ImageIcon } from "lucide-react";
import ReactMarkdown from "react-markdown";
import api from "@/lib/api";
import { DataTag } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AiUsagePanel from "@/components/AiUsagePanel";

const WELCOME = "Olá! Sou o Assistente ANM IA. Posso ajudar com procedimentos minerários, legislação, requerimentos, pesquisa mineral, lavra, RAL, DIPEM, Guia de Utilização e demais obrigações. Você também pode anexar documentos e imagens (licenças, publicações, mapas) para eu analisar — e, quando faltar base oficial, pesquiso na web e cito as fontes.";
const SUGGESTIONS = [
    "Como requerer uma área para pesquisa mineral?",
    "Meu relatório final de pesquisa foi aprovado. Qual a próxima etapa?",
    "Quais documentos são necessários para requerimento de lavra?",
    "Como funciona a Guia de Utilização?",
    "Quais são as obrigações após a publicação do Alvará de Pesquisa?",
    "Explique a diferença entre autorização de pesquisa e concessão de lavra.",
];

export default function Assistant() {
    const [messages, setMessages] = useState([{ id: "welcome", role: "assistant", content: WELCOME, sources: [] }]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [convId, setConvId] = useState(null);
    const [usage, setUsage] = useState(null);
    const [processes, setProcesses] = useState([]);
    const [procNum, setProcNum] = useState("");
    const [packs, setPacks] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const fileInput = useRef(null);
    const endRef = useRef(null);

    useEffect(() => { api.get("/ai/usage").then((r) => setUsage(r.data)).catch(() => {}); }, []);
    useEffect(() => { api.get("/processes").then((r) => setProcesses(r.data)).catch(() => {}); }, []);
    useEffect(() => { api.get("/payments/addon-packs").then((r) => setPacks(r.data)).catch(() => {}); }, []);
    useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

    const reached = usage?.franchise_reached;
    const fmt = (n) => (n ?? 0).toLocaleString("pt-BR");

    const buyAddon = async (pack) => {
        try {
            const r = await api.post("/payments/checkout-addon", { origin_url: window.location.origin, pack });
            window.location.href = r.data.checkout_url;
        } catch (e) { toast.error("Não foi possível iniciar a compra do pacote."); }
    };

    const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.gif,.bmp,.docx,.txt,.csv,.md,.xlsx,.xlsm,image/*";
    const handleFiles = async (fileList) => {
        const chosen = Array.from(fileList || []);
        if (!chosen.length) return;
        if (attachments.length + chosen.length > 5) {
            toast.error("Máximo de 5 arquivos por mensagem."); return;
        }
        const tooBig = chosen.find((f) => f.size > 20 * 1024 * 1024);
        if (tooBig) { toast.error(`"${tooBig.name}" excede 20 MB.`); return; }
        setUploading(true);
        try {
            const form = new FormData();
            chosen.forEach((f) => form.append("files", f));
            const r = await api.post("/ai/attachments", form);
            setAttachments((a) => [...a, ...(r.data.attachments || [])]);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Não foi possível anexar o(s) arquivo(s).");
        } finally {
            setUploading(false);
            if (fileInput.current) fileInput.current.value = "";
        }
    };
    const removeAttachment = (id) => setAttachments((a) => a.filter((x) => x.id !== id));

    const send = async (text) => {
        const q = (text ?? input).trim();
        if ((!q && !attachments.length) || busy || reached) return;
        setInput("");
        const attachIds = attachments.map((a) => a.id);
        const attachNames = attachments.map((a) => a.name);
        setMessages((m) => [...m, { id: crypto.randomUUID(), role: "user", content: q, attachments: attachNames }]);
        setAttachments([]);
        setBusy(true);
        try {
            const res = await api.post("/ai/chat", { message: q || "Analise o(s) anexo(s).", conversation_id: convId, process_number: procNum || null, attachment_ids: attachIds.length ? attachIds : null });
            setConvId(res.data.conversation_id);
            setUsage(res.data.usage);
            if (res.data.usage?.warn_80 && !res.data.usage?.franchise_reached) {
                toast.warning("Você utilizou 80% da sua franquia mensal de IA.");
            }
            setMessages((m) => [...m, { id: crypto.randomUUID(), role: "assistant", content: res.data.answer, sources: res.data.sources || [], usedWeb: res.data.used_web }]);
        } catch (e) {
            if (e.response?.status === 429) {
                toast.error(e.response.data.detail);
                setUsage((u) => ({ ...(u || {}), franchise_reached: true }));
                setMessages((m) => [...m, { id: crypto.randomUUID(), role: "assistant", content: e.response.data.detail, sources: [] }]);
            } else {
                toast.error("Erro ao consultar o Assistente ANM IA.");
                setMessages((m) => [...m, { id: crypto.randomUUID(), role: "assistant", content: "Ocorreu um erro ao gerar a resposta. Tente novamente.", sources: [] }]);
            }
        } finally { setBusy(false); }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-4" data-testid="assistant-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl md:text-3xl font-black text-slate-900 tracking-tight flex items-center gap-2"><Bot className="w-7 h-7 text-petroleum" /> Assistente ANM IA</h1>
                    <p className="text-slate-500 mt-1 text-sm">Respostas fundamentadas em fontes oficiais. Anexe documentos e imagens (PDF, JPG/PNG, DOCX, planilhas) para análise e o assistente pesquisa na web quando necessário.</p>
                </div>
            </div>

            <AiUsagePanel usage={usage} />

            <div className="border border-slate-200 bg-white rounded-md flex flex-col h-[62vh]">
                <div className="bg-petroleum text-white px-4 py-3 flex items-center gap-2 rounded-t-md">
                    <Bot className="w-5 h-5 text-mineral" />
                    <span className="font-heading font-bold text-sm">Assistente ANM IA</span>
                    <DataTag kind="ia" />
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((m) => (
                        <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.role}`}>
                            <div className={`max-w-[85%] p-3 rounded-md text-sm ${m.role === "user" ? "bg-petroleum text-white rounded-tr-none" : "bg-petroleum/5 text-slate-900 rounded-tl-none border border-petroleum/10"}`}>
                                {m.role === "assistant" ? (
                                    <div className="prose prose-sm max-w-none prose-headings:font-heading prose-headings:text-petroleum prose-headings:text-sm prose-a:text-petroleum prose-strong:text-slate-900 leading-relaxed">
                                        <ReactMarkdown>{m.content}</ReactMarkdown>
                                    </div>
                                ) : (
                                    <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>
                                )}
                                {m.role === "user" && m.attachments?.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1" data-testid="chat-user-attachments">
                                        {m.attachments.map((n, i) => (
                                            <span key={i} className="inline-flex items-center gap-1 text-[11px] bg-white/15 rounded px-1.5 py-0.5"><Paperclip className="w-3 h-3" />{n}</span>
                                        ))}
                                    </div>
                                )}
                                {m.role === "assistant" && m.usedWeb && (
                                    <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="chat-web-badge">
                                        <Globe className="w-3 h-3" /> Pesquisado na web
                                    </div>
                                )}
                                {m.sources?.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-petroleum/10">
                                        <div className="text-[11px] font-bold uppercase tracking-wide text-petroleum mb-1">Fontes oficiais e de referência</div>
                                        <div className="flex flex-col gap-1">
                                            {m.sources.map((s, j) => (
                                                <a key={s.url || j} href={s.url} target="_blank" rel="noreferrer" className="text-xs text-slate-600 hover:text-petroleum inline-flex items-center gap-1">
                                                    <ExternalLink className="w-3 h-3" /> {s.source} — {s.title}
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {busy && (
                        <div className="flex justify-start"><div className="bg-petroleum/5 border border-petroleum/10 p-3 rounded-md rounded-tl-none"><Loader2 className="w-5 h-5 text-petroleum animate-spin" /></div></div>
                    )}
                    <div ref={endRef} />
                </div>

                {messages.length <= 1 && !reached && (
                    <div className="px-4 pb-3 flex flex-wrap gap-2">
                        {SUGGESTIONS.map((s) => (
                            <button key={s} onClick={() => send(s)} disabled={reached} data-testid="ai-suggestion" className="text-xs text-left text-petroleum bg-petroleum/5 hover:bg-petroleum/10 border border-petroleum/15 rounded-full px-3 py-1.5 transition-colors disabled:opacity-50">{s}</button>
                        ))}
                    </div>
                )}

                {usage?.warn_80 && !reached && (
                    <div className="px-4 py-2 bg-yellow-50 border-t border-yellow-200 text-xs text-yellow-800 font-medium" data-testid="ai-warn-80">
                        Você utilizou 80% da sua franquia mensal de IA.
                    </div>
                )}
                {reached && (
                    <div className="px-4 py-3 bg-red-50 border-t border-red-200 space-y-2" data-testid="ai-franchise-reached">
                        <span className="block text-xs text-red-700 font-medium">Franquia mensal atingida. Adquira um pacote adicional ou aguarde a renovação.</span>
                        <div className="flex flex-wrap gap-2">
                            {packs.map((p) => (
                                <button key={p.pack} onClick={() => buyAddon(p.pack)} data-testid={`buy-addon-${p.pack}`}
                                    className="bg-mineral hover:bg-mineralHover text-white rounded-sm px-3 py-2 text-xs font-bold transition-colors">
                                    +{fmt(p.tokens)} tokens · R$ {fmt(p.price)}
                                </button>
                            ))}
                        </div>
                        <span className="block text-[11px] text-slate-500">Quanto maior o pacote, menor o custo por token.</span>
                    </div>
                )}
                <div className="p-3 border-t border-slate-200 space-y-2">
                    {attachments.length > 0 && (
                        <div className="flex flex-wrap gap-1.5" data-testid="ai-attachment-chips">
                            {attachments.map((a) => (
                                <span key={a.id} data-testid={`ai-attachment-chip-${a.id}`} className="inline-flex items-center gap-1 text-[11px] bg-petroleum/5 border border-petroleum/15 text-petroleum rounded-full pl-2 pr-1 py-0.5">
                                    {a.kind === "image" ? <ImageIcon className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                                    <span className="max-w-[160px] truncate" title={a.name}>{a.name}</span>
                                    <button onClick={() => removeAttachment(a.id)} title="Remover anexo" data-testid={`ai-attachment-remove-${a.id}`} className="text-petroleum/60 hover:text-red-600"><X className="w-3 h-3" /></button>
                                </span>
                            ))}
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-400 shrink-0">Processo:</span>
                        <Select value={procNum || "none"} onValueChange={(v) => setProcNum(v === "none" ? "" : v)}>
                            <SelectTrigger className="h-8 text-xs w-64" data-testid="ai-process-select"><SelectValue placeholder="Nenhum (geral)" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">Nenhum (consulta geral)</SelectItem>
                                {processes.map((p) => <SelectItem key={p.id} value={p.process_number}>{p.process_number} — {p.substance}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex items-center gap-2">
                        <input ref={fileInput} type="file" multiple accept={ACCEPT} className="hidden" data-testid="ai-file-input"
                            onChange={(e) => handleFiles(e.target.files)} />
                        <button onClick={() => fileInput.current?.click()} disabled={busy || reached || uploading || attachments.length >= 5}
                            title="Anexar arquivos ou imagens (PDF, JPG/PNG, DOCX, TXT, planilhas — até 5, 20 MB cada)" data-testid="ai-attach-button"
                            className="bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-md p-2.5 transition-colors disabled:opacity-50 shrink-0">
                            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
                        </button>
                        <input
                            data-testid="ai-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && send()}
                            disabled={reached}
                            placeholder={reached ? "Franquia mensal atingida." : "Pergunte sobre mineração/ANM — anexe documentos ou imagens para análise..."}
                            className="flex-1 px-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-petroleum/20 focus:border-petroleum transition-colors disabled:opacity-60"
                        />
                        <button onClick={() => send()} disabled={busy || reached || uploading} data-testid="ai-send-button" className="bg-mineral hover:bg-mineralHover text-white rounded-md p-2.5 transition-colors disabled:opacity-60">
                            <Send className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex items-start gap-2 text-xs text-slate-500 bg-mineral/5 border border-mineral/20 rounded-md p-3">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-mineralHover" />
                As respostas da IA possuem caráter informativo e não substituem análise técnica, jurídica ou manifestação oficial da ANM.
            </div>
        </div>
    );
}
